import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MAX_LENGTH_TEXTAREA } from '~/common/constants/form';
import { Button } from '~/eds/src/components/Button/Button';
import { CheckboxGroups } from '~/eds/src/components/CheckboxGroups/CheckboxGroups';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Panel_DEPRECATED } from '~/eds/src/components/Panel/Panel_DEPRECATED';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { RoleType } from '~/enums/Roles';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useErrors } from '~/hooks/useErrors';
import { NOT_IMPLEMENTED_PERMISSIONS } from '~/permissions/rbac/constants';
import { api } from '~/redux/api/createApi';
import {
  PermissionGroupAttributes,
  RoleAttributes,
} from '~/redux/api/methods/roles/roles';
import { Nullable } from '~/types/Nullable';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { openInNewTab } from '~/utils/browser';

import { trackSegment } from '../SegmentAnalytics';
import { showToast } from '../Shared/EcToast/showToast';

type Props = {
  visible: boolean;
  isDuplicate?: boolean;
  onHide: () => void;
  role: Nullable<RoleAttributes>;
  clientId?: string;
};

type PermissionGroupName = string;
type PermissionsIds = string[];
type PermissionsDict = Nullable<Record<PermissionGroupName, PermissionsIds>>;

export const RoleFormPanel = ({
  visible,
  role,
  isDuplicate,
  onHide,
  clientId,
}: Props) => {
  const intl = useIntl();
  const { RETRY_ERROR } = useErrors();
  const user = useCurrentUser();
  const [isEnlarged, toggleIsEnlarged] = useToggle(false);
  const [search, setSearch] = useState<Nullable<string>>('');
  const [permissionsDict, setPermissionsDict] = useState<PermissionsDict>({});
  const [debouncedName, name, setName] =
    useDebouncedState<Nullable<string>>('');
  const [description, setDescription] = useState<Nullable<string>>('');
  const permissionsList = Object.values(permissionsDict ?? {}).flat();
  const hasPremiumPermissions = useFlag(FlagType.PremiumPermissions);

  const enableModalConsolidation = useFlag(FlagType.ModalConsolidation);

  const { data: permissionGroups, isFetching } =
    api.endpoints.getPermissions.useQuery({});

  const [createRole, createRoleResult] = api.endpoints.createRole.useMutation();
  const { isLoading: isLoadingCreate } = createRoleResult;

  const [updateRole, updateRoleResult] = api.endpoints.updateRole.useMutation();
  const { isLoading: isLoadingRoles } = updateRoleResult;

  const [validateRoleName, validateRoleNameResult] =
    api.endpoints.validateRoleName.useLazyQuery();
  const { isLoading: isValidatingRoleName, data: validateRoleNameData } =
    validateRoleNameResult;

  const { isUnique: isRoleNameUnique } = validateRoleNameData?.attributes ?? {};

  const additionalPermissions = useMemo(() => {
    const rolePermissions = role?.permissions ?? [];
    return NOT_IMPLEMENTED_PERMISSIONS.filter((permission) =>
      rolePermissions.includes(permission),
    );
  }, [role?.permissions]);

  const noChanges = !role
    ? !name && !description && permissionsList.length === 0
    : name === role.name &&
      description === role.description &&
      permissionsList.length === role.permissions?.length &&
      permissionsList.every((permission) =>
        role.permissions?.includes(permission),
      );

  const isDebouncedNameValid = debouncedName !== null && debouncedName !== '';
  const shouldValidateRoleName =
    isDuplicate || !role
      ? isDebouncedNameValid
      : isDebouncedNameValid &&
        role?.name.toLowerCase() !== debouncedName?.toLowerCase();

  useEffect(() => {
    if (shouldValidateRoleName) {
      validateRoleName({ name: debouncedName! });
    }
  }, [debouncedName, shouldValidateRoleName]);

  const hasRoleNameError =
    isDuplicate || !role
      ? isRoleNameUnique === false
      : isRoleNameUnique === false && name !== role?.name;

  const isSaveDisabled =
    !name || permissionsList.length === 0 || noChanges || hasRoleNameError;

  const isPreBuiltAdmin =
    role?.name?.toLowerCase().includes('admin') &&
    role.role_type === RoleType.PreBuiltRole;

  const handleSubmitRole = useCallback(async () => {
    const params = {
      name: name!,
      description: description ?? '',
      permissions: role
        ? [...permissionsList, ...additionalPermissions]
        : permissionsList,
      client_id: clientId ?? user?.client?.toString(),
    };
    if (isDuplicate || !role) {
      try {
        await createRole(params).unwrap();
        trackSegment(isDuplicate ? 'duplicateRole' : 'createRole', {
          ...params,
          user_id: user.id,
          client_id: user.client,
        });
        handleCreateSuccess();
      } catch {
        showToast(ERROR, RETRY_ERROR);
      }
    } else {
      try {
        await updateRole({ roleId: role.id, params }).unwrap();
        trackSegment('updateRole', {
          ...params,
          user_id: user.id,
          client_id: user.client,
        });
        handleUpdateSuccess();
      } catch {
        showToast(ERROR, RETRY_ERROR);
      }
    }
  }, [
    isDuplicate,
    role,
    permissionsList,
    additionalPermissions,
    name,
    description,
    user,
  ]);

  const handleHide = () => {
    setName('');
    setDescription('');
    setPermissionsDict({});
    onHide();
  };

  const handleCreateSuccess = () => {
    showToast(
      SUCCESS,
      intl.formatMessage(
        {
          id: 'RoleFormPanel.createSuccessMessage',
          defaultMessage: '"{name}" was successfully created',
          description:
            'Success message when role is created. Example: name = "Admin Role"',
        },
        { name },
      ),
    );
    handleHide();
  };

  const handleUpdateSuccess = () => {
    showToast(
      SUCCESS,
      intl.formatMessage(
        {
          id: 'RoleFormPanel.updateSuccessMessage',
          defaultMessage: '"{name}" was successfully updated',
          description:
            'Success message when role is updated. Example: name = "Admin Role"',
        },
        { name },
      ),
    );
    handleHide();
  };

  useEffect(() => {
    if (role) {
      setName(isDuplicate ? `${role.name} (1)` : role.name);
      setDescription(role.description);
      const initialPermissionsDict = role.permissions?.reduce(
        (dict, permission) => {
          const groupName = permissionGroups?.find((group) =>
            group.permissions.some((p) => p.id === permission),
          )?.name;
          if (groupName) {
            dict[groupName] = [...(dict[groupName] ?? []), permission];
          }
          return dict;
        },
        {} as Record<PermissionGroupName, PermissionsIds>,
      );
      setPermissionsDict(initialPermissionsDict);
    }
  }, [role, permissionGroups, isDuplicate]);

  const nonDuplicateTitle = role
    ? intl.formatMessage(
        {
          id: 'RoleFormPanel.editRoleTitle',
          defaultMessage: 'Edit {roleName}',
          description:
            'Title for editing an existing role. Example: roleName = "Admin Role"',
        },
        { roleName: role.name },
      )
    : intl.formatMessage({
        id: 'RoleFormPanel.newRoleTitle',
        defaultMessage: 'New Role',
        description: 'Title for creating a new role',
      });
  const title = isDuplicate
    ? intl.formatMessage(
        {
          id: 'RoleFormPanel.duplicateRoleTitle',
          defaultMessage: 'Duplicate {roleName}',
          description:
            'Title for duplicating a role. Example: roleName = "Admin Role"',
        },
        {
          roleName:
            role?.name ??
            intl.formatMessage({
              id: 'RoleFormPanel.roleDefaultName',
              defaultMessage: 'Role',
              description: 'Default role name fallback',
            }),
        },
      )
    : nonDuplicateTitle;

  const children = (
    <>
      {' '}
      <Layout direction="column" spacing="2">
        {!hasPremiumPermissions && (
          <StatusMessage
            message={intl.formatMessage({
              id: 'RoleFormPanel.premiumPermissionsMessage',
              defaultMessage:
                'Custom roles are available with the Advanced Administration add-on. Contact your CSM to learn more.',
              description: 'Message about premium permissions requirement',
            })}
            status="active"
            action={{
              text: intl.formatMessage({
                id: 'RoleFormPanel.clickHereAction',
                defaultMessage: 'Click here',
                description: 'Click here action text',
              }),
              onClick: () => openInNewTab('mailto:success@evisort.com'),
            }}
          />
        )}
        {isPreBuiltAdmin && (
          <StatusMessage
            title={intl.formatMessage({
              id: 'RoleFormPanel.adminPermissionsTitle',
              defaultMessage:
                'Some Administrator permissions cannot be duplicated',
              description: 'Title for admin permissions warning',
            })}
            message={intl.formatMessage({
              id: 'RoleFormPanel.adminPermissionsMessage',
              defaultMessage:
                'Administrators can access all folders, documents and Workflow tickets; can manage folder access settings; and, can manage sharing settings for shared saved searches and custom dashboards. To grant these permissions, assign a user to the Administrator role.',
              description: 'Message about admin permissions limitations',
            })}
            action={{
              text: intl.formatMessage({
                id: 'RoleFormPanel.learnMoreAction',
                defaultMessage: 'Learn more',
                description: 'Learn more action text',
              }),
              onClick: () => {
                openInNewTab(
                  'https://support.evisort.com/hc/en-us/articles/360042492434-Admin-How-To-Set-User-Permissioning-with-Access-Levels-and-Departments#h_de1ecf9e-e395-4937-8bca-ad207b1ad2e9',
                );
              },
            }}
          ></StatusMessage>
        )}
        {isDuplicate ? (
          <Text>
            {intl.formatMessage({
              id: 'RoleFormPanel.duplicateDescription',
              defaultMessage:
                'Duplicate and modify role information and permissions.',
              description: 'Description for duplicating a role',
            })}
          </Text>
        ) : (
          <Text>
            {role
              ? intl.formatMessage({
                  id: 'RoleFormPanel.editDescription',
                  defaultMessage:
                    'Edit custom role information and permissions.',
                  description: 'Description for editing a role',
                })
              : intl.formatMessage({
                  id: 'RoleFormPanel.createDescription',
                  defaultMessage:
                    'Create a custom role to apply to users in your organization.',
                  description: 'Description for creating a role',
                })}
          </Text>
        )}

        <Text variant="section">
          {intl.formatMessage({
            id: 'RoleFormPanel.generalInformationSection',
            defaultMessage: 'General Information',
            description: 'General information section title',
          })}
        </Text>

        <Layout direction="column" spacing="1">
          <FormField
            input={TextInput}
            label={intl.formatMessage({
              id: 'RoleFormPanel.roleNameLabel',
              defaultMessage: 'Role name',
              description: 'Label for role name field',
            })}
            placeholder={intl.formatMessage({
              id: 'RoleFormPanel.roleNamePlaceholder',
              defaultMessage: 'e.g. User Account Admin',
              description: 'Placeholder for role name field',
            })}
            name="name"
            value={name}
            required={true}
            onChange={(name) => setName(name as string)}
            error={
              hasRoleNameError && isDebouncedNameValid
                ? intl.formatMessage(
                    {
                      id: 'RoleFormPanel.roleNameExistsError',
                      defaultMessage:
                        'The {roleName} role already exists. Looks like you want to assigning users to an existing role instead of creating a New Role.',
                      description:
                        'Error message when role name already exists. Example: roleName = "Admin"',
                    },
                    { roleName: debouncedName },
                  )
                : ''
            }
            disabled={!hasPremiumPermissions}
          />
        </Layout>
        <Layout direction="column" spacing="1">
          <Text variant="body-bold">
            <FormattedMessage
              id="RoleFormPanel.roleDescriptionLabel"
              defaultMessage="Role Description <optional>(Optional)</optional>"
              description="Label for role description field"
              values={{
                optional: (chunks) => (
                  <Text variant="tiny" color="text.secondary">
                    {chunks}
                  </Text>
                ),
              }}
            />
          </Text>
          <Text preset="description">
            {intl.formatMessage({
              id: 'RoleFormPanel.roleDescriptionHelper',
              defaultMessage:
                'Add a clear description to explain the purpose of the role for reference.',
              description: 'Helper text for role description field',
            })}
          </Text>
          <TextArea
            placeholder={intl.formatMessage({
              id: 'RoleFormPanel.roleDescriptionPlaceholder',
              defaultMessage:
                'e.g. Manages user accounts and system permissions',
              description: 'Placeholder for role description field',
            })}
            name="description"
            value={description}
            onChange={setDescription}
            maxLength={MAX_LENGTH_TEXTAREA}
            disabled={!hasPremiumPermissions}
          ></TextArea>
          <Text variant="tiny" color="text.secondary" fontWeight="500">
            {intl.formatMessage(
              {
                id: 'RoleFormPanel.maxCharactersHelper',
                defaultMessage: 'Maximum {maxLength} characters',
                description:
                  'Helper text showing maximum character limit. Example: maxLength = "500"',
              },
              { maxLength: MAX_LENGTH_TEXTAREA },
            )}
          </Text>
        </Layout>
      </Layout>
      <Layout mt="6" direction="column" spacing="2">
        <Text variant="section">
          {intl.formatMessage({
            id: 'RoleFormPanel.permissionSettingsSection',
            defaultMessage: 'Permission Settings',
            description: 'Permission settings section title',
          })}
        </Text>
        <Text>
          {intl.formatMessage({
            id: 'RoleFormPanel.permissionSettingsDescription',
            defaultMessage:
              'Select the permissions you wish to include in this custom role.',
            description: 'Description for permission settings section',
          })}
        </Text>
        <SearchInput
          width="100%"
          name="search"
          value={search}
          onChange={setSearch}
          placeholder={intl.formatMessage({
            id: 'RoleFormPanel.searchPermissionsPlaceholder',
            defaultMessage: 'Search for permission...',
            description: 'Placeholder for permission search field',
          })}
        />

        <CheckboxGroups
          mode="descriptive"
          groups={(permissionGroups ?? [])
            .map((group) => asOptionGroup(group, hasPremiumPermissions))
            .map((group) => ({
              ...group,
              disabled: !hasPremiumPermissions,
            }))}
          value={permissionsDict}
          onChange={setPermissionsDict}
          search={search ?? ''}
        />
      </Layout>
    </>
  );

  if (enableModalConsolidation && visible) {
    return (
      <Panel
        title={title}
        mode="floating"
        width="560px"
        hidden={{ onHide: handleHide }}
        enableMaximize
        enableBackdrop
        footer={{
          actions: [
            {
              mode: 'chip',
              text: intl.formatMessage({
                id: 'RoleFormPanel.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              }),
              onClick: handleHide,
            },
            {
              mode: 'chip',
              text: intl.formatMessage({
                id: 'RoleFormPanel.saveButton',
                defaultMessage: 'Save',
                description: 'Save button text',
              }),
              onClick: handleSubmitRole,
              level: 'primary',
              isLoading:
                isLoadingCreate || isLoadingRoles || isValidatingRoleName,
              disabled: isSaveDisabled,
            },
          ],
        }}
        children={children}
        loadingContent={{
          isLoading: isFetching,
        }}
      />
    );
  }

  return (
    <Panel_DEPRECATED
      actions={[
        {
          icon: 'maximize',
          isActive: isEnlarged,
          label: isEnlarged
            ? intl.formatMessage({
                id: 'RoleFormPanel.condenseLabel',
                defaultMessage: 'Condense',
                description: 'Label for condense panel action',
              })
            : intl.formatMessage({
                id: 'RoleFormPanel.enlargeLabel',
                defaultMessage: 'Enlarge',
                description: 'Label for enlarge panel action',
              }),
          value: isEnlarged ? 'Condense' : 'Enlarge',
          onClick: toggleIsEnlarged,
        },
      ]}
      isVisible={visible}
      width={isEnlarged ? 'fill' : 'l'}
      title={title}
      onHide={handleHide}
      isPadded={true}
      mode="blocking"
      isLoading={isFetching}
      footer={
        <Layout spacing="2" justify="flex-end" borderTop="border" p="3">
          <Button
            variant="tertiary"
            text={intl.formatMessage({
              id: 'RoleFormPanel.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            })}
            onClick={handleHide}
          />
          <Button
            variant="primary"
            text={intl.formatMessage({
              id: 'RoleFormPanel.saveButton',
              defaultMessage: 'Save',
              description: 'Save button text',
            })}
            onClick={handleSubmitRole}
            isLoading={
              isLoadingCreate || isLoadingRoles || isValidatingRoleName
            }
            disabled={isSaveDisabled}
          />
        </Layout>
      }
    >
      {children}
    </Panel_DEPRECATED>
  );
};

const asOptionGroup = (
  permission: PermissionGroupAttributes,
  hasPremiumPermissions = false,
) => {
  return {
    label: permission.name,
    name: permission.name,
    options: permission.permissions.map((permission) => ({
      label: permission.name,
      description: permission.description,
      value: permission.id,
      disabled: hasPremiumPermissions ? false : !!permission.premium,
    })),
  };
};
